import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertCircle, Loader2, Minus, Plus, Ticket } from "lucide-react";
import { RegistrationGroup } from "../../api/getEventTransactionDetails/types";
import { returnEventVouchers } from "../../api/returnEventVouchers/returnEventVouchersApi";
import { useToast } from "@/hooks/useToast";

interface VoucherReturnPanelProps {
  group: RegistrationGroup;
  onCancel: () => void;
  onSuccess: () => void;
}

const RETURN_REASONS = [
  { value: "attendee_cancellation", label: "Attendee Cancellation" },
  { value: "duplicate_registration", label: "Duplicate Registration" },
  { value: "change_in_plans", label: "Change in Plans" },
  { value: "administrative_adjustment", label: "Administrative Adjustment" },
  { value: "event_cancellation", label: "Event Cancellation" },
  { value: "other", label: "Other (specify in notes)" },
] as const;

export function VoucherReturnPanel({
  group,
  onCancel,
  onSuccess,
}: VoucherReturnPanelProps) {
  const { toast } = useToast();

  // Calculate available vouchers to return (not already returned)
  const availableVouchers = group.voucherUsageHistory.filter(
    (v) => !v.isReturned
  ).length;

  const [vouchersToReturn, setVouchersToReturn] = useState(
    Math.min(1, availableVouchers)
  );
  const [reason, setReason] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [notifyVoucherAdmin, setNotifyVoucherAdmin] = useState(true);
  const [notifyEventCoordinator, setNotifyEventCoordinator] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleIncrement = () => {
    if (vouchersToReturn < availableVouchers) {
      setVouchersToReturn(vouchersToReturn + 1);
    }
  };

  const handleDecrement = () => {
    if (vouchersToReturn > 1) {
      setVouchersToReturn(vouchersToReturn - 1);
    }
  };

  const handleSubmit = async () => {
    setError(null);

    if (!reason) {
      setError("Please select a reason for returning the vouchers");
      return;
    }

    if (vouchersToReturn <= 0 || vouchersToReturn > availableVouchers) {
      setError("Invalid number of vouchers to return");
      return;
    }

    try {
      setIsSubmitting(true);

      await returnEventVouchers({
        uuid: group.id,
        vouchersToReturn,
        reason: reason as any,
        notes: notes.trim() || null,
        notifyVoucherAdmin,
        notifyEventCoordinator,
      });

      toast({
        title: "Vouchers returned",
        description: `${vouchersToReturn} voucher${vouchersToReturn > 1 ? "s" : ""} returned successfully`,
      });

      onSuccess();
    } catch (err: any) {
      setError(
        err.response?.data?.message || err.message || "Failed to return vouchers"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (availableVouchers === 0) {
    return (
      <div className="rounded-lg border p-4 bg-muted/30">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No vouchers available to return. All vouchers have already been returned.
          </AlertDescription>
        </Alert>
        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={onCancel}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border p-4 bg-muted/30">
      <div className="space-y-4">
        {/* Header Info */}
        <div className="flex items-center gap-2 text-sm">
          <Ticket className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">
            {availableVouchers} voucher{availableVouchers > 1 ? "s" : ""} available to return
          </span>
        </div>

        <Separator />

        {/* Voucher Count Selector */}
        <div>
          <Label className="text-sm mb-2">Number of Vouchers to Return</Label>
          <div className="flex items-center gap-3 mt-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleDecrement}
              disabled={vouchersToReturn <= 1}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <div className="flex flex-col items-center justify-center min-w-[80px] px-4 py-2 border rounded-md bg-background">
              <span className="text-2xl font-semibold">{vouchersToReturn}</span>
              <span className="text-xs text-muted-foreground">
                voucher{vouchersToReturn !== 1 ? "s" : ""}
              </span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleIncrement}
              disabled={vouchersToReturn >= availableVouchers}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Maximum: {availableVouchers} voucher{availableVouchers > 1 ? "s" : ""}
          </p>
        </div>

        {/* Reason */}
        <div>
          <Label htmlFor="reason" className="text-sm">
            Reason for Return *
          </Label>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger className="mt-1.5">
              <SelectValue placeholder="Select a reason..." />
            </SelectTrigger>
            <SelectContent>
              {RETURN_REASONS.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Notes */}
        <div>
          <Label htmlFor="notes" className="text-sm">
            Notes (Optional)
          </Label>
          <Textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any additional context or explanation..."
            className="mt-1.5"
            rows={3}
          />
        </div>

        <Separator />

        {/* Notification Options */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Notification Options</Label>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="notifyVoucherAdmin"
              checked={notifyVoucherAdmin}
              onCheckedChange={(checked) =>
                setNotifyVoucherAdmin(checked as boolean)
              }
            />
            <Label
              htmlFor="notifyVoucherAdmin"
              className="text-sm font-normal cursor-pointer"
            >
              Notify voucher administrator
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="notifyEventCoordinator"
              checked={notifyEventCoordinator}
              onCheckedChange={(checked) =>
                setNotifyEventCoordinator(checked as boolean)
              }
            />
            <Label
              htmlFor="notifyEventCoordinator"
              className="text-sm font-normal cursor-pointer"
            >
              Notify event coordinator
            </Label>
          </div>
        </div>

        {/* Info Alert */}
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-xs">
            Returning vouchers will credit them back to the company's voucher pool.
            The registration remains active and no payment refund will be processed.
          </AlertDescription>
        </Alert>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !reason}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Return {vouchersToReturn} Voucher{vouchersToReturn > 1 ? "s" : ""}
          </Button>
        </div>
      </div>
    </div>
  );
}
